import React, { useState, useEffect, forwardRef } from 'react';
import { toast } from 'react-toastify';
import { FaImage, FaTrash, FaStar, FaUpload, FaCheck, FaTimes, FaPhone } from 'react-icons/fa';

// ─────────────────────────────────────────────
// Reusable UI Components
// ─────────────────────────────────────────────

const Button = ({
  children,
  type = 'button',
  variant = 'primary',
  loading = false,
  disabled = false,
  onClick,
  className = '',
  ...props
}) => {
  const baseClass =
    'inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-200 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 ' +
    'transform hover:scale-105 active:scale-95 shadow-lg';

  const variants = {
    primary:
      'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white focus:ring-amber-500 shadow-amber-200',
    outline:
      'border-2 border-gray-300 bg-white hover:bg-gray-50 text-gray-700 focus:ring-gray-500 shadow-sm',
    danger:
      'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white focus:ring-red-500 shadow-red-200',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClass} ${variants[variant] || variants.primary} ${className}`}
      {...props}
    >
      {loading && (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
      )}
      {children}
    </button>
  );
};

const Input = forwardRef(
  ({ label, error, required = false, className = '', type = 'text', ...props }, ref) => (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-semibold text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <input
        ref={ref}
        type={type}
        className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all shadow-sm ${
          error ? 'border-red-500 focus:ring-red-500' : ''
        } ${className}`}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-600 mt-1 flex items-center">
          <FaTimes className="mr-1" />
          {error}
        </p>
      )}
    </div>
  )
);
Input.displayName = 'Input';

const Loading = ({ size = 'md' }) => {
  const sizes = { sm: 'h-5 w-5', md: 'h-8 w-8', lg: 'h-12 w-12' };
  return (
    <div className="flex items-center justify-center">
      <div className={`${sizes[size]} border-4 border-amber-500 border-t-transparent rounded-full animate-spin`} />
    </div>
  );
};

// ─────────────────────────────────────────────
// Section Header
// ─────────────────────────────────────────────
const SectionHeader = ({ icon: Icon = FaCheck, title }) => (
  <div className="flex items-center gap-3 pb-4 border-b-2 border-amber-200">
    <div className="p-2 bg-amber-100 rounded-lg">
      <Icon className="text-amber-600" />
    </div>
    <h3 className="text-xl font-bold text-gray-900">{title}</h3>
  </div>
);

// ─────────────────────────────────────────────
// Single-Image Upload (drag & drop)
// ─────────────────────────────────────────────
const SingleImageUpload = ({ preview, onFileChange, onRemove, existingLabel = null }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid format. Please select JPEG, PNG, or WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must not exceed 5 MB.');
      return;
    }
    onFileChange(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="space-y-4">
      {/* Preview area */}
      {preview ? (
        <div className="relative group">
          <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200 shadow-sm">
            <img src={preview} alt="Preview" className="w-full h-full object-cover" />
          </div>

          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={onRemove}
              className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 flex items-center justify-center"
            >
              <FaTrash className="mr-2" />
              Remove Image
            </button>
          </div>

          {/* Badge: existing vs new */}
          {existingLabel && (
            <div className="absolute bottom-2 left-2 bg-blue-600 bg-opacity-90 text-white text-xs px-3 py-1 rounded-lg font-semibold shadow">
              {existingLabel}
            </div>
          )}
        </div>
      ) : (
        /* Drop-zone */
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            dragOver
              ? 'border-amber-500 bg-amber-50'
              : 'border-gray-300 hover:border-amber-400 bg-white'
          }`}
        >
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files[0])}
            className="hidden"
            id="service-image-upload"
          />
          <label htmlFor="service-image-upload" className="cursor-pointer">
            <div className="text-gray-600">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 mb-4">
                <FaUpload className="text-amber-600 text-2xl" />
              </div>
              <p className="text-base font-medium mb-1">
                <span className="text-amber-600 hover:text-amber-700">Click to upload</span> or drag &amp; drop
              </p>
              <p className="text-sm text-gray-500">PNG, JPG, WebP up to 5MB</p>
            </div>
          </label>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Pure helpers (outside component for stable
// references — no need in useEffect deps)
// ─────────────────────────────────────────────
const USD_RATE = 16000;

const calculateUSD = (idrValue) => {
  if (!idrValue && idrValue !== 0) return '';
  const num = parseFloat(idrValue.toString().replace(/[^\d.]/g, ''));
  if (isNaN(num)) return '';
  return (num / USD_RATE).toFixed(2);
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return '0';
  const num = parseFloat(value.toString().replace(/[^\d.]/g, ''));
  if (isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

// ─────────────────────────────────────────────
// ServiceForm — MAIN
// ─────────────────────────────────────────────
const ServiceForm = ({ service, onSave, onCancel, serviceTypes }) => {
  // ── state ──
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price_idr: '',
    price_usd: '',          // auto-calculated
    phone: '',
    service_type: 'other',
    status: 'active',
    featured: false,
    image: null,
  });

  const [imagePreview, setImagePreview]           = useState(null);
  const [existingImagePath, setExistingImagePath] = useState(null);
  const [existingPublicId, setExistingPublicId]   = useState(null);
  const [isNewImage, setIsNewImage]               = useState(false);
  const [errors, setErrors]                       = useState({});
  const [loading, setLoading]                     = useState(false);

  // ── auto-calculate USD every time price_idr changes ──
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      price_usd: calculateUSD(prev.price_idr),
    }));
  }, [formData.price_idr]);

  // ── load existing service (edit mode) ──
  useEffect(() => {
    if (!service) return;

    setFormData({
      title:        service.title        || '',
      description:  service.description  || '',
      price_idr:    service.price_idr    || '',
      price_usd:    calculateUSD(service.price_idr), // recalculate from IDR
      phone:        service.phone        || '',
      service_type: service.service_type || 'other',
      status:       service.status       || 'active',
      featured:     Boolean(service.featured === 1 || service.featured === true),
      image:        null,
    });

    if (service.image_path) {
      setExistingImagePath(service.image_path);
      setExistingPublicId(service.cloudinary_public_id || null);
      setImagePreview(service.image_path);
      setIsNewImage(false);
    } else {
      setExistingImagePath(null);
      setExistingPublicId(null);
      setImagePreview(null);
    }
  }, [service]);

  // ── handlers ──
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleImageFile = (file) => {
    setFormData((prev) => ({ ...prev, image: file }));
    const reader = new FileReader();
    reader.onload   = (e) => setImagePreview(e.target.result);
    reader.onerror  = ()  => toast.error('Failed to read the image file.');
    reader.readAsDataURL(file);
    setIsNewImage(true);
    if (errors.image) setErrors((prev) => ({ ...prev, image: '' }));
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    setIsNewImage(false);
    // If in edit mode → revert to existing image
    if (service && existingImagePath) {
      setImagePreview(existingImagePath);
    } else {
      setImagePreview(null);
      setExistingImagePath(null);
      setExistingPublicId(null);
    }
  };

  // ── validation ──
  const validateForm = () => {
    const errs = {};
    if (!formData.title.trim())       errs.title       = 'Title is required';
    if (!formData.description.trim()) errs.description = 'Description is required';

    if (formData.price_idr && isNaN(Number(formData.price_idr)))
      errs.price_idr = 'IDR price must be a valid number';

    if (formData.phone && formData.phone.trim().length < 10)
      errs.phone = 'Phone number must be at least 10 characters';

    const validTypes = serviceTypes.map((t) => t.value);
    if (!validTypes.includes(formData.service_type))
      errs.service_type = 'Please select a valid service type';

    // Image is required only on CREATE
    if (!service && !formData.image)
      errs.image = 'Service image is required for new service';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();

      // Required fields
      fd.append('title',        formData.title.trim());
      fd.append('description',  formData.description.trim());
      fd.append('service_type', formData.service_type);
      fd.append('status',       formData.status);
      fd.append('featured',     formData.featured ? 'true' : 'false');

      // Optional fields
      if (formData.price_idr && formData.price_idr !== '')
        fd.append('price_idr', formData.price_idr);
      if (formData.price_usd && formData.price_usd !== '')
        fd.append('price_usd', formData.price_usd);
      if (formData.phone && formData.phone.trim() !== '')
        fd.append('phone', formData.phone.trim());

      // Image
      if (formData.image instanceof File) {
        fd.append('image', formData.image);
      } else if (!service) {
        toast.error('Service image is required');
        setLoading(false);
        return;
      }

      await onSave(fd);
    } catch (error) {
      console.error('Form submission error:', error);
      if (error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach((err) => {
          serverErrors[err.param] = err.msg;
        });
        setErrors(serverErrors);
        toast.error('Validation errors occurred');
      } else {
        toast.error(error.response?.data?.message || 'Failed to save service');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── helper: render <select> ──
  const renderSelect = (label, name, options, required = false) => (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-gray-900 transition-all shadow-sm hover:border-amber-400"
      >
        <option value="">Select {label}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {errors[name] && (
        <p className="text-sm text-red-600 mt-1 flex items-center">
          <FaTimes className="mr-1" />
          {errors[name]}
        </p>
      )}
    </div>
  );

  // ── canSubmit ──
  const canSubmit = () => {
    const hasRequired = formData.title.trim() && formData.description.trim() && formData.service_type;
    const hasImage    = !!imagePreview; // existing or new
    return hasRequired && hasImage && !loading;
  };

  // ── status & service-type options ──
  const statusOptions = [
    { value: 'active',   label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  // ── badge label for image ──
  const imageBadge = isNewImage
    ? 'New image — ready to upload'
    : existingImagePath
      ? 'Current Cloudinary image'
      : null;

  // ─────────────────── RENDER ───────────────────
  return (
    <div className="max-w-5xl mx-auto p-8 bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50">
      <form onSubmit={handleSubmit} className="space-y-8">

        {/* ═══════════ 1. Basic Information ═══════════ */}
        <div className="space-y-6">
          <SectionHeader title="Basic Information" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <Input
                label="Service Title"
                required
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Example: Private Airport Transfer"
                error={errors.title}
                maxLength={100}
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                placeholder="Describe the service in detail..."
                className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-vertical shadow-sm transition-all ${
                  errors.description ? 'border-red-500 focus:ring-red-500' : ''
                }`}
              />
              {errors.description && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <FaTimes className="mr-1" /> {errors.description}
                </p>
              )}
            </div>

            {/* Service Type */}
            {renderSelect('Service Type', 'service_type', serviceTypes, true)}

            {/* Status */}
            {renderSelect('Status', 'status', statusOptions, true)}
          </div>
        </div>

        {/* ═══════════ 2. Pricing ═══════════ */}
        <div className="space-y-6">
          <SectionHeader title="Service Pricing" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* IDR — EDITABLE */}
            <div className="space-y-4 p-5 bg-gradient-to-br from-amber-50 to-white rounded-xl border-2 border-amber-400">
              <h4 className="text-base font-bold text-gray-800 flex items-center gap-2">
                Price (IDR) <span className="text-xs font-normal text-amber-600">(Enter price here)</span>
              </h4>
              <Input
                label="Price (IDR)"
                type="number"
                name="price_idr"
                value={formData.price_idr}
                onChange={handleInputChange}
                placeholder="500000"
                min="0"
                step="1000"
                error={errors.price_idr}
              />
            </div>

            {/* USD — AUTO CALCULATED */}
            <div className="space-y-4 p-5 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-200">
              <h4 className="text-base font-bold text-gray-800 flex items-center gap-2">
                Price (USD) <span className="text-xs font-normal text-blue-600">(Auto-calculated: IDR ÷ 16,000)</span>
              </h4>
              <Input
                label="Price (USD)"
                type="number"
                step="0.01"
                value={formData.price_usd}
                placeholder="Auto calculated"
                min="0"
                readOnly
                className="bg-gray-50"
              />
            </div>
          </div>

          {/* Info box */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <FaCheck className="text-amber-600 mt-1 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-bold mb-1">Auto-Calculation Note:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>USD price is automatically calculated using the rate <strong>1 USD = 16,000 IDR</strong>.</li>
                <li>Update the IDR price and the USD price will refresh automatically.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ═══════════ 3. Contact Information ═══════════ */}
        <div className="space-y-6">
          <SectionHeader icon={FaPhone} title="Contact Information" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label="Phone Number"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+62 812 3456 7890"
                error={errors.phone}
              />
            </div>
          </div>
        </div>

        {/* ═══════════ 4. Settings ═══════════ */}
        <div className="space-y-6">
          <SectionHeader title="Status & Settings" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-3 pt-2">
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={formData.featured}
                onChange={handleInputChange}
                className="h-5 w-5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded cursor-pointer"
              />
              <label htmlFor="featured" className="text-sm font-medium text-gray-900 select-none cursor-pointer flex items-center gap-2">
                <FaStar className="text-amber-500" />
                Featured Service (Displayed on homepage)
              </label>
            </div>
          </div>
        </div>

        {/* ═══════════ 5. Service Image ═══════════ */}
        <div className="space-y-6">
          <SectionHeader icon={FaImage} title="Service Image" />

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Upload Image {!service && <span className="text-red-500">*</span>}
            </label>
            <SingleImageUpload
              preview={imagePreview}
              onFileChange={handleImageFile}
              onRemove={handleRemoveImage}
              existingLabel={imageBadge}
            />
            {errors.image && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <FaTimes className="mr-1" /> {errors.image}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-3 flex items-center gap-2">
              <FaImage className="text-amber-600" />
              {service
                ? 'Upload a new image to replace the current one. Formats: JPG, PNG, WebP. Max 5MB.'
                : 'Upload at least 1 image. Formats: JPG, PNG, WebP. Max 5MB.'}
            </p>
          </div>
        </div>

        {/* ═══════════ 6. Form Summary ═══════════ */}
        <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-6 shadow-inner">
          <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FaCheck className="text-amber-600" />
            Form Summary:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
            {[
              ['Title',         formData.title        || 'Not filled'],
              ['Service Type',  serviceTypes.find((t) => t.value === formData.service_type)?.label || 'Not selected'],
              ['Status',        formData.status       || 'Active'],
              ['Price (IDR)',   `IDR ${formatCurrency(formData.price_idr)}`],
              ['Price (USD)',   `$${formData.price_usd || '0.00'}`],
              ['Phone',         formData.phone        || 'Not provided'],
              ['Featured',      formData.featured     ? 'Yes' : 'No'],
              ['Image',         imagePreview          ? (isNewImage ? 'New image ready' : 'Existing image') : 'No image'],
            ].map(([key, val]) => (
              <div key={key} className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span>
                  <strong>{key}:</strong> {val}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════ Actions ═══════════ */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t-2 border-gray-200">
          <div className="text-sm text-gray-600 font-medium">
            {service ? (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-lg">
                <FaCheck className="text-amber-600" />
                Edit Mode: Service ID {service.id}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-lg">
                <FaCheck className="text-green-600" />
                Add New Service Mode
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!canSubmit()}
              className="w-full sm:w-auto"
            >
              {loading ? 'Saving...' : service ? 'Update Service' : 'Save Service'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ServiceForm;